/**
 * §6.10 — the chart of accounts.
 *
 * WHY THIS EXISTS
 *
 * `POST /accounts` and `PATCH /accounts/:id` have existed since Phase 1c, were
 * tested, and nothing in the application called either. The chart was whatever
 * `seed_chart_of_accounts` produced, and an account that turned out to be
 * missing or misnamed had no way to be added or fixed short of psql.
 *
 * DEACTIVATE, NEVER DELETE
 *
 * §6.10's convention, shared with parties. An account carrying posted entries
 * cannot be removed without orphaning them -- migration 0002's trigger refuses
 * it outright -- so it is deactivated: it stops being offered for new coding
 * and stays on everything it is already on. `postedLineCount` sits next to the
 * control so the difference between tidying away an unused account and hiding
 * one that is in the books is visible BEFORE the click.
 *
 * WHAT THIS SCREEN WILL NOT LET YOU CHANGE
 *
 * `type` and `normalBalance`, on an account that already exists. Both are
 * absent from the server's updateSchema for the same reason they are absent
 * here: changing either reverses the sign of every historical balance and can
 * move an account between the balance sheet and the P&L, with no correct
 * migration for the entries already posted to it. The fix is a new account and
 * a reclassifying journal entry, which leaves a trail.
 *
 * `accountRole` is not offered at all -- see CreateAccountBody in api.ts.
 *
 * TWO BALANCES, AND THEY ARE NOT THE SAME NUMBER
 *
 * `balanceCents` is the account's OWN balance. `rollupBalanceCents` is that
 * account plus every descendant. This screen shows the own balance always and
 * the rollup only where there are children to roll up -- showing one number and
 * calling it "the balance" on a chart with parents is how a total gets read as
 * a leaf and reconciled against the wrong thing.
 *
 * Both are signed by `normalBalance`, so an expense with debits reads positive,
 * and both count POSTED lines only. A draft moves nothing here, which is what
 * makes a number on this screen comparable to a bank statement.
 */

import { keepsNetAssets, projectWord, useBusiness, useCan, useKeepsGoods } from '../auth/AuthProvider.js';
import { Link } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Toast } from '../components/Toast.js';
import { ApiError, api, type Account, type ContributionKind, type CreateAccountBody, type InventoryRole } from '../api.js';

const TYPES: Array<[Account['type'], string]> = [
  ['asset', 'Asset'],
  ['liability', 'Liability'],
  ['equity', 'Equity'],
  ['revenue', 'Revenue'],
  ['expense', 'Expense'],
];

/**
 * The conventional normal balance for a type.
 *
 * A DEFAULT, not a derivation, and that distinction is the whole reason the
 * field stays on the form. Assets and expenses are debit-normal and everything
 * else credit-normal -- until accumulated depreciation, which is an asset that
 * lives on the credit side. Deriving this silently would be right almost
 * always and wrong invisibly.
 */
export function defaultNormalBalance(type: Account['type']): Account['normalBalance'] {
  return type === 'asset' || type === 'expense' ? 'debit' : 'credit';
}

function formatCents(cents: string | number): string {
  return (Number(cents) / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

/** 0084: what each contribution kind is called, in a sentence and as an option. */
const CONTRIBUTION_LABELS: Record<ContributionKind, string> = {
  cash: 'cash gifts',
  in_kind: 'in-kind gifts',
  event: 'event gifts',
  pledge: 'pledges',
};
const CONTRIBUTION_TITLES: Record<ContributionKind, string> = {
  cash: 'Cash Gifts',
  in_kind: 'In-Kind Gifts',
  event: 'Event Gifts',
  pledge: 'Pledges',
};

export function AccountsPage() {
  // What this screen offers (0069); the API refuses the rest either way.
  const can = useCan();
  const mayChange = can('manage_business');
  // What a project is called here (0081): Program for a non-profit.
  const business = useBusiness();
  const projectLabel = projectWord(business);
  // 0084 (§B.17.3): what an account holds of donors' giving, a non-profit's to mark.
  const marksGiving = keepsNetAssets(business);
  // 0086: Cost Of Sales is offered once the business keeps goods; Hold Stock on any asset of its own.
  const keepsGoods = useKeepsGoods();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftCode, setDraftCode] = useState('');
  const [draftName, setDraftName] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api
      .listAccounts()
      .then((result) => {
        setAccounts(result.accounts);
        setError(null);
      })
      .catch((caught: unknown) =>
        setError(caught instanceof ApiError ? caught.message : 'Could not load the chart.'),
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  /*
   * Filtered in the browser, not on the server. GET /accounts returns the whole
   * chart in one request because the editor needs it that way, and a business
   * with a few hundred accounts is not a paging problem. A search endpoint here
   * would be a second code path returning a different shape of the same rows.
   */
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return accounts
      .filter((account) => includeInactive || account.isActive)
      .filter(
        (account) =>
          needle === '' ||
          account.code.toLowerCase().includes(needle) ||
          account.name.toLowerCase().includes(needle),
      );
  }, [accounts, search, includeInactive]);

  async function create(body: CreateAccountBody) {
    setBusy(true);
    setError(null);
    try {
      const { account } = await api.createAccount(body);
      setAccounts((current) => [...current, account]);
      setNotice(`Added ${account.code} ${account.name}`);
      setAdding(false);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not add that account.');
    } finally {
      setBusy(false);
    }
  }

  async function rename(account: Account) {
    const code = draftCode.trim();
    const name = draftName.trim();
    if (code === '' || name === '') {
      setError('An account needs a code and a name.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { account: updated } = await api.updateAccount(account.id, { code, name });
      setAccounts((current) =>
        current.map((candidate) => (candidate.id === updated.id ? updated : candidate)),
      );
      setNotice(`Renamed ${updated.code} ${updated.name}`);
      setEditingId(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not save that account.');
    } finally {
      setBusy(false);
    }
  }

  /** 0084 (§B.17.3): a gift's kind on revenue, Pledge on a receivable; the giving report reads it. */
  async function setContributionKind(account: Account, contributionKind: ContributionKind | null) {
    setBusy(true);
    setError(null);
    try {
      const { account: updated } = await api.updateAccount(account.id, { contributionKind });
      setAccounts((current) =>
        current.map((candidate) => (candidate.id === updated.id ? { ...candidate, contributionKind: updated.contributionKind } : candidate)),
      );
      setNotice(`${updated.code} ${updated.name}: ${contributionKind === null ? 'holds no giving' : `holds ${CONTRIBUTION_LABELS[contributionKind]}`}`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not change that account.');
    } finally {
      setBusy(false);
    }
  }

  /** 0086 (the inventory plan's §3.4): an asset of its own may hold stock, an expense take a sale's cost. */
  async function setInventoryRole(account: Account, inventoryRole: InventoryRole | null) {
    setBusy(true);
    setError(null);
    try {
      const { account: updated } = await api.updateAccount(account.id, { inventoryRole });
      setAccounts((current) =>
        current.map((candidate) => (candidate.id === updated.id ? { ...candidate, inventoryRole: updated.inventoryRole } : candidate)),
      );
      setNotice(
        `${updated.code} ${updated.name}: ${
          inventoryRole === 'stock' ? 'holds stock' : inventoryRole === 'cost_of_sales' ? 'takes the cost of sales' : 'holds no stock'
        }${
          // Round 130: the session reads whether the business keeps goods when it starts.
          inventoryRole === 'stock' && !keepsGoods ? '. Items appears in the menu the next time you sign in.' : ''
        }`,
      );
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not change that account.');
    } finally {
      setBusy(false);
    }
  }

  /** 0081 (§C.2): an asset of the normal role may hold a project's cost, so its lines carry a project. */
  async function setTracksProjects(account: Account, tracksProjects: boolean) {
    setBusy(true);
    setError(null);
    try {
      const { account: updated } = await api.updateAccount(account.id, { tracksProjects });
      setAccounts((current) =>
        current.map((candidate) => (candidate.id === updated.id ? { ...candidate, tracksProjects: updated.tracksProjects } : candidate)),
      );
      setNotice(`${updated.code} ${updated.name} ${tracksProjects ? `now tracks ${projectLabel.toLowerCase()}s` : `no longer tracks ${projectLabel.toLowerCase()}s`}`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not change that account.');
    } finally {
      setBusy(false);
    }
  }

  async function setActive(account: Account, isActive: boolean) {
    setBusy(true);
    setError(null);
    try {
      const { account: updated } = await api.updateAccount(account.id, { isActive });
      setAccounts((current) =>
        current.map((candidate) => (candidate.id === updated.id ? updated : candidate)),
      );
      setNotice(`${isActive ? 'Reactivated' : 'Deactivated'} ${updated.code} ${updated.name}`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not change that account.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="accounts">
      <div className="row header accounts-head">
        <h1>Chart of accounts</h1>
        <span className="muted">{visible.length} shown</span>
      </div>

      <div className="row accounts-filters">
        <label>
          Search{' '}
          <input
            value={search}
            placeholder="code or name"
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <label>
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(event) => setIncludeInactive(event.target.checked)}
          />{' '}
          Show inactive
        </label>
        {!adding && mayChange && (
          <button type="button" className="link" onClick={() => setAdding(true)}>
            Add an account
          </button>
        )}
      </div>

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {notice && !error && (
        <Toast message={notice} onDismiss={() => setNotice(null)} variant="ok" />
      )}

      {adding && (
        <NewAccountForm busy={busy} onCancel={() => setAdding(false)} onCreate={create} />
      )}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : (
        <table className="lines accounts-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Type</th>
              <th className="num">Balance</th>
              <th className="num">With children</th>
              <th className="num">Posted lines</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.map((account) => (
              <tr key={account.id} className={account.isActive ? undefined : 'is-inactive'}>
                {editingId === account.id ? (
                  <>
                    <td>
                      <input
                        aria-label={`Code for ${account.name}`}
                        value={draftCode}
                        onChange={(event) => setDraftCode(event.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        aria-label={`Name for ${account.code}`}
                        value={draftName}
                        onChange={(event) => setDraftName(event.target.value)}
                      />
                    </td>
                  </>
                ) : (
                  <>
                    <td>{account.code}</td>
                    <td>{account.name}</td>
                  </>
                )}
                <td className="muted">{account.type}</td>
                {/*
                  The balance opens the account register: every posted line
                  behind it, with no dates, because this balance has none.
                  Only where there is a line to open.
                */}
                <td className="num">
                  {account.postedLineCount > 0 ? (
                    <Link
                      to={`/reports/account?${new URLSearchParams({ account: account.id, report: 'accounts' }).toString()}`}
                      aria-label={`Lines behind ${account.code} ${account.name}`}
                    >
                      {formatCents(account.balanceCents)}
                    </Link>
                  ) : (
                    formatCents(account.balanceCents)
                  )}
                </td>
                {/*
                  Blank rather than a repeat when there is nothing below the
                  account. A parent and a leaf showing the same figure twice
                  invites the reader to treat one of them as a subtotal.
                */}
                <td className="num muted">
                  {account.childCount > 0 ? formatCents(account.rollupBalanceCents) : ''}
                </td>
                {/*
                  Next to the deactivate control on purpose. It is the
                  difference between tidying away an account nobody used and
                  hiding one that is in the books, and it has to be visible
                  before the click rather than explained after it.
                */}
                <td className="num muted">{account.postedLineCount}</td>
                <td className="line-actions">
                  {!mayChange ? null : editingId === account.id ? (
                    <>
                      <button
                        type="button"
                        className="link"
                        disabled={busy}
                        onClick={() => void rename(account)}
                      >
                        Save
                      </button>
                      <button type="button" className="link" onClick={() => setEditingId(null)}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="link"
                        aria-label={`Rename ${account.code}`}
                        onClick={() => {
                          setEditingId(account.id);
                          setDraftCode(account.code);
                          setDraftName(account.name);
                        }}
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        className="link"
                        disabled={busy}
                        aria-label={`${account.isActive ? 'Deactivate' : 'Reactivate'} ${account.code}`}
                        onClick={() => void setActive(account, !account.isActive)}
                      >
                        {account.isActive ? 'Deactivate' : 'Reactivate'}
                      </button>
                      {account.type === 'asset' && account.accountRole === 'normal' && (
                        <button
                          type="button"
                          className="link"
                          disabled={busy}
                          aria-label={`${account.tracksProjects ? 'Stop Tracking' : 'Track'} ${projectLabel}s on ${account.code}`}
                          onClick={() => void setTracksProjects(account, account.tracksProjects !== true)}
                        >
                          {account.tracksProjects ? `Stop Tracking ${projectLabel}s` : `Track ${projectLabel}s`}
                        </button>
                      )}
                      {account.type === 'asset' && account.accountRole === 'normal' && (
                        <button
                          type="button"
                          className="link"
                          disabled={busy}
                          aria-label={`${account.inventoryRole === 'stock' ? 'Stop Holding Stock' : 'Hold Stock'} on ${account.code}`}
                          onClick={() => void setInventoryRole(account, account.inventoryRole === 'stock' ? null : 'stock')}
                        >
                          {account.inventoryRole === 'stock' ? 'Stop Holding Stock' : 'Hold Stock'}
                        </button>
                      )}
                      {keepsGoods && account.type === 'expense' && (
                        <button
                          type="button"
                          className="link"
                          disabled={busy}
                          aria-label={`${account.inventoryRole === 'cost_of_sales' ? 'Not Cost Of Sales' : 'Cost Of Sales'} on ${account.code}`}
                          onClick={() => void setInventoryRole(account, account.inventoryRole === 'cost_of_sales' ? null : 'cost_of_sales')}
                        >
                          {account.inventoryRole === 'cost_of_sales' ? 'Not Cost Of Sales' : 'Cost Of Sales'}
                        </button>
                      )}
                      {marksGiving && (account.type === 'revenue' || (account.type === 'asset' && account.accountRole === 'ar_control')) && (
                        <select
                          className="contribution-kind"
                          aria-label={`Contribution Kind for ${account.code}`}
                          disabled={busy}
                          value={account.contributionKind ?? ''}
                          onChange={(event) => {
                            const chosen = event.target.value;
                            void setContributionKind(account, chosen === '' ? null : (chosen as ContributionKind));
                          }}
                        >
                          <option value="">No Giving</option>
                          {(account.type === 'revenue' ? (['cash', 'in_kind', 'event'] as const) : (['pledge'] as const)).map((kind) => (
                            <option key={kind} value={kind}>
                              {CONTRIBUTION_TITLES[kind]}
                            </option>
                          ))}
                        </select>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/**
 * The add form. Also used by the editor, which creates accounts inline.
 *
 * `normalBalance` follows the type until the operator touches it, and then
 * stops moving. A field that keeps resetting itself under the cursor is worse
 * than one that never helps.
 */
export function NewAccountForm({
  busy,
  onCancel,
  onCreate,
  initialName = '',
}: {
  busy: boolean;
  onCancel: () => void;
  onCreate: (body: CreateAccountBody) => void;
  initialName?: string;
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState(initialName);
  const [type, setType] = useState<Account['type']>('expense');
  const [normalBalance, setNormalBalance] = useState<Account['normalBalance']>('debit');
  const [touchedBalance, setTouchedBalance] = useState(false);

  function chooseType(next: Account['type']) {
    setType(next);
    if (!touchedBalance) setNormalBalance(defaultNormalBalance(next));
  }

  const ready = code.trim() !== '' && name.trim() !== '';

  return (
    <div className="new-account">
      <label className="field">
        <span>Code</span>
        <input
          aria-label="Account code"
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
      </label>
      <label className="field">
        <span>Name</span>
        <input
          aria-label="Account name"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      <label className="field">
        <span>Type</span>
        <select
          aria-label="Account type"
          value={type}
          onChange={(event) => chooseType(event.target.value as Account['type'])}
        >
          {TYPES.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Normal balance</span>
        <select
          aria-label="Normal balance"
          value={normalBalance}
          onChange={(event) => {
            setTouchedBalance(true);
            setNormalBalance(event.target.value as Account['normalBalance']);
          }}
        >
          <option value="debit">Debit</option>
          <option value="credit">Credit</option>
        </select>
      </label>

      <div className="row">
        <button
          type="button"
          disabled={!ready || busy}
          onClick={() => onCreate({ code: code.trim(), name: name.trim(), type, normalBalance })}
        >
          Add account
        </button>
        <button type="button" className="link" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
